import { lazy, Suspense, useEffect, useId, useMemo, useState } from "react";
import remarkParse from "remark-parse";
import { unified } from "unified";

import type { AgentStoredDocument, XLMModel } from "../api/e2e";
import type { ModelReference } from "../lib/modelReferences";

const AgentSummaryMarkdownDocument = lazy(
  () => import("./AgentSummaryMarkdownDocument"),
);

const SUMMARY_SECTION_TITLES = [
  "Business Structure",
  "Core Thesis",
  "Forecast Methodology",
  "Guidance and Consensus",
  "Risks and Unresolved Items",
] as const;

type SummarySectionTitle = (typeof SUMMARY_SECTION_TITLES)[number];

interface SummarySection {
  title: SummarySectionTitle;
  source: string;
}

interface StructuredSummary {
  preamble: string;
  sections: SummarySection[];
}

const ATX_HEADING = /^ {0,3}(#{1,6})(?:[\t ]+(.*)|[\t ]*)$/u;
const TRAILING_HEADING_MARKS = /[\t ]+#+[\t ]*$/u;
const FENCE_PREFIX = /^ {0,3}(`{3,}|~{3,})(.*)$/u;
const SETEXT_UNDERLINE = /^ {0,3}(=+|-+)[\t ]*$/u;
const THEMATIC_BREAK =
  /^ {0,3}(?:(?:\*[\t ]*){3,}|(?:-[\t ]*){3,}|(?:_[\t ]*){3,})$/u;
const CONTAINER_BLOCK = /^ {0,3}(?:>|(?:[-+*]|\d{1,9}[.)])(?:[\t ]+|$))/u;
const INLINE_MARKDOWN = /[\\*_`\[\]<>~]/u;

interface MarkdownAstNode {
  type: string;
  depth?: number;
  value?: string;
  children?: MarkdownAstNode[];
  position?: {
    start: { line?: number };
  };
}

interface OutlineHeading {
  node: MarkdownAstNode;
  parent: MarkdownAstNode;
}

type SummaryIdentity = Pick<
  XLMModel,
  "ticker" | "company_name" | "reporting_currency"
>;

const markdownOutlineParser = unified().use(remarkParse);

function collectOutlineHeadings(
  node: MarkdownAstNode,
  parent: MarkdownAstNode | undefined,
  headings: OutlineHeading[],
) {
  if (
    parent &&
    node.type === "heading" &&
    (node.depth === 1 || node.depth === 2)
  ) {
    headings.push({ node, parent });
  }
  node.children?.forEach((child) =>
    collectOutlineHeadings(child, node, headings),
  );
}

function plainNodeText(node: MarkdownAstNode) {
  const child = node.children?.[0];
  return node.children?.length === 1 && child?.type === "text"
    ? child.value
    : undefined;
}

function isAtxHeading(sourceLines: string[], node: MarkdownAstNode) {
  const line = node.position?.start.line;
  const match = line ? ATX_HEADING.exec(sourceLines[line - 1] ?? "") : null;
  return Boolean(match?.[1] && match[1].length === node.depth);
}

function hasSubstantiveContent(node: MarkdownAstNode): boolean {
  if (
    node.type === "heading" ||
    node.type === "thematicBreak" ||
    node.type === "definition"
  ) {
    return false;
  }
  if (node.children) {
    return node.children.some(hasSubstantiveContent);
  }
  return node.type !== "root";
}

function hasExactMarkdownOutline(source: string) {
  const root = markdownOutlineParser.parse(
    source,
  ) as unknown as MarkdownAstNode;
  const headings: OutlineHeading[] = [];
  collectOutlineHeadings(root, undefined, headings);
  const expectedDepths = [1, 2, 2, 2, 2, 2];
  const sourceLines = source.split(/\r?\n/u);
  const rootChildren = root.children ?? [];

  if (
    headings.length !== expectedDepths.length ||
    headings.some(
      ({ node, parent }, index) =>
        parent !== root ||
        node.depth !== expectedDepths[index] ||
        !isAtxHeading(sourceLines, node),
    ) ||
    rootChildren[0] !== headings[0]?.node ||
    rootChildren[1]?.type !== "paragraph" ||
    rootChildren[2] !== headings[1]?.node ||
    plainNodeText(rootChildren[1] ?? { type: "" }) === undefined ||
    headings
      .slice(1)
      .some(
        ({ node }, index) =>
          plainNodeText(node) !== SUMMARY_SECTION_TITLES[index],
      )
  ) {
    return false;
  }

  const sectionHeadings = headings.slice(1);
  for (const [index, { node }] of sectionHeadings.entries()) {
    const sectionStart = rootChildren.indexOf(node) + 1;
    const nextHeading = sectionHeadings[index + 1]?.node;
    const sectionEnd = nextHeading
      ? rootChildren.indexOf(nextHeading)
      : rootChildren.length;
    if (
      sectionStart <= 0 ||
      sectionEnd < sectionStart ||
      !rootChildren.slice(sectionStart, sectionEnd).some(hasSubstantiveContent)
    ) {
      return false;
    }
  }

  return true;
}

interface MarkdownLine {
  index: number;
  text: string;
  headingLevel?: number;
  headingText?: string;
  headingSyntax?: "atx" | "setext";
  fenceMarker?: boolean;
  setextCandidate?: boolean;
}

function parsedMarkdownLines(lines: string[]): MarkdownLine[] {
  const parsed: MarkdownLine[] = [];
  let openFence: { character: string; length: number } | null = null;

  lines.forEach((rawLine, index) => {
    const fenceMatch = FENCE_PREFIX.exec(rawLine);
    const candidateMarker = fenceMatch?.[1];
    const remainder = fenceMatch?.[2] ?? "";
    const marker =
      candidateMarker?.startsWith("`") && remainder.includes("`")
        ? undefined
        : candidateMarker;
    if (openFence) {
      if (
        marker?.startsWith(openFence.character) &&
        marker.length >= openFence.length &&
        remainder.trim().length === 0
      ) {
        parsed.push({ index, text: rawLine.trim(), fenceMarker: true });
        openFence = null;
      }
      return;
    }
    if (marker) {
      parsed.push({ index, text: rawLine.trim(), fenceMarker: true });
      openFence = {
        character: marker[0] ?? "",
        length: marker.length,
      };
      return;
    }
    if (!rawLine.trim()) return;

    const setextMatch = SETEXT_UNDERLINE.exec(rawLine);
    const previous = parsed.at(-1);
    if (
      setextMatch &&
      previous &&
      previous.index === index - 1 &&
      previous.headingLevel === undefined &&
      !previous.fenceMarker &&
      previous.setextCandidate
    ) {
      parsed[parsed.length - 1] = {
        ...previous,
        headingLevel: setextMatch[1]?.startsWith("=") ? 1 : 2,
        headingText: previous.text,
        headingSyntax: "setext",
      };
      return;
    }

    const headingMatch = ATX_HEADING.exec(rawLine);
    const marks = headingMatch?.[1];
    if (!marks) {
      const text = rawLine.trim();
      parsed.push({
        index,
        text,
        setextCandidate:
          rawLine.length - rawLine.trimStart().length <= 3 &&
          !CONTAINER_BLOCK.test(rawLine) &&
          !THEMATIC_BREAK.test(rawLine),
      });
      return;
    }
    parsed.push({
      index,
      text: rawLine.trim(),
      headingLevel: marks.length,
      headingText: (headingMatch[2] ?? "")
        .replace(TRAILING_HEADING_MARKS, "")
        .trim(),
      headingSyntax: "atx",
    });
  });

  return parsed;
}

function validTitle(
  value: string | undefined,
  identity: SummaryIdentity | undefined,
) {
  if (!value) return false;
  if (identity?.ticker && identity.company_name) {
    return value === `${identity.ticker} — ${identity.company_name}`;
  }
  const separator = value.indexOf(" — ");
  return (
    separator > 0 &&
    value.slice(0, separator).trim().length > 0 &&
    value.slice(separator + 3).trim().length > 0
  );
}

function validSubtitle(value: string, identity: SummaryIdentity | undefined) {
  const parts = value.split(" · ");
  return (
    parts.length === 3 &&
    parts[0] === "Financial Model Summary" &&
    Boolean(parts[1]?.trim()) &&
    Boolean(parts[2]?.trim()) &&
    !INLINE_MARKDOWN.test(parts[1] ?? "") &&
    !INLINE_MARKDOWN.test(parts[2] ?? "") &&
    !parts[1]?.includes("·") &&
    !parts[2]?.includes("·") &&
    (!identity?.reporting_currency ||
      parts[2] === `${identity.reporting_currency} millions`)
  );
}

function hasSectionContent(lines: MarkdownLine[], start: number, end: number) {
  return lines.some(
    (line) =>
      line.index > start &&
      line.index < end &&
      line.headingLevel === undefined &&
      !line.fenceMarker &&
      !THEMATIC_BREAK.test(line.text),
  );
}

export function parseStructuredAgentSummary(
  source: string,
  identity?: SummaryIdentity,
): StructuredSummary | null {
  if (!hasExactMarkdownOutline(source)) return null;

  const lines = source.split(/\r?\n/u);
  const markdownLines = parsedMarkdownLines(lines);
  const firstLevel = markdownLines.filter((line) => line.headingLevel === 1);
  const headings = markdownLines.filter((line) => line.headingLevel === 2);
  const firstHeading = headings[0];
  const firstLevelHeading = firstLevel[0];
  const preamble = firstHeading
    ? markdownLines.filter((line) => line.index < firstHeading.index)
    : [];

  if (
    !firstHeading ||
    !firstLevelHeading ||
    firstLevel.length !== 1 ||
    firstLevelHeading.headingSyntax !== "atx" ||
    !validTitle(firstLevelHeading.headingText, identity) ||
    preamble.length !== 2 ||
    preamble[0] !== firstLevelHeading ||
    preamble[1]?.headingLevel !== undefined ||
    !validSubtitle(preamble[1]?.text ?? "", identity) ||
    headings.length !== SUMMARY_SECTION_TITLES.length ||
    headings.some(
      (heading, index) =>
        heading.headingSyntax !== "atx" ||
        heading.headingText !== SUMMARY_SECTION_TITLES[index],
    )
  ) {
    return null;
  }

  const sections: SummarySection[] = [];
  for (const [index, title] of SUMMARY_SECTION_TITLES.entries()) {
    const heading = headings[index];
    const nextHeading = headings[index + 1];
    if (!heading) return null;
    const sectionEnd = nextHeading?.index ?? lines.length;
    if (!hasSectionContent(markdownLines, heading.index, sectionEnd)) {
      return null;
    }
    sections.push({
      title,
      source: lines
        .slice(heading.index + 1, sectionEnd)
        .join("\n")
        .trim(),
    });
  }

  return {
    preamble: lines.slice(0, firstHeading.index).join("\n").trim(),
    sections,
  };
}

export default function AgentSummaryMarkdown({
  source,
  documents,
  referenceState,
  model,
  modelReferenceState,
  openingDocumentId,
  onOpenDocument,
  onOpenModelReference,
}: {
  source: string;
  documents: AgentStoredDocument[];
  referenceState: "loading" | "ready" | "error";
  model: XLMModel | undefined;
  modelReferenceState: "loading" | "ready" | "error";
  openingDocumentId: string | null;
  onOpenDocument: (document: AgentStoredDocument) => void;
  onOpenModelReference: (reference: ModelReference) => void;
}) {
  const structuredSummary = useMemo(
    () => parseStructuredAgentSummary(source, model),
    [model, source],
  );
  const disclosureId = useId();
  const [disclosureState, setDisclosureState] = useState<{
    source: string;
    expanded: ReadonlySet<SummarySectionTitle>;
  }>({ source, expanded: new Set() });
  useEffect(() => {
    setDisclosureState({ source, expanded: new Set() });
  }, [source]);
  const expanded =
    disclosureState.source === source
      ? disclosureState.expanded
      : new Set<SummarySectionTitle>();
  const markdownProps = {
    documents,
    referenceState,
    model,
    modelReferenceState,
    openingDocumentId,
    onOpenDocument,
    onOpenModelReference,
  };

  return (
    <div className="agent-summary-panel__markdown">
      <Suspense fallback={<p>Formatting summary…</p>}>
        {structuredSummary ? (
          <>
            {structuredSummary.preamble && (
              <div className="agent-summary-accordion__preamble">
                <AgentSummaryMarkdownDocument
                  source={structuredSummary.preamble}
                  {...markdownProps}
                />
              </div>
            )}
            <div
              className="agent-summary-accordion"
              role="group"
              aria-label="Financial model summary sections"
            >
              {structuredSummary.sections.map((section, index) => {
                const isExpanded = expanded.has(section.title);
                const sectionKey = section.title
                  .toLowerCase()
                  .replaceAll(" ", "-");
                const headingId = `${disclosureId}-${sectionKey}-heading`;
                const panelId = `${disclosureId}-${sectionKey}-panel`;
                return (
                  <section
                    className="agent-summary-accordion__section"
                    data-expanded={isExpanded ? "true" : "false"}
                    key={section.title}
                  >
                    <h2 className="agent-summary-accordion__heading">
                      <button
                        id={headingId}
                        type="button"
                        aria-controls={panelId}
                        aria-expanded={isExpanded}
                        onClick={() => {
                          setDisclosureState((currentState) => {
                            const nextExpanded = new Set(
                              currentState.source === source
                                ? currentState.expanded
                                : [],
                            );
                            if (nextExpanded.has(section.title)) {
                              nextExpanded.delete(section.title);
                            } else {
                              nextExpanded.add(section.title);
                            }
                            return { source, expanded: nextExpanded };
                          });
                        }}
                      >
                        <span
                          className="agent-summary-accordion__number"
                          aria-hidden="true"
                        >
                          {index + 1}.
                        </span>
                        <span className="agent-summary-accordion__title">
                          {section.title}
                        </span>
                        <svg
                          className="agent-summary-accordion__chevron"
                          viewBox="0 0 12 12"
                          width="14"
                          height="14"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.4"
                          aria-hidden="true"
                        >
                          <path d="m3 4.75 3 3 3-3" />
                        </svg>
                      </button>
                    </h2>
                    <div
                      id={panelId}
                      className="agent-summary-accordion__panel"
                      role="region"
                      aria-labelledby={headingId}
                      hidden={!isExpanded}
                    >
                      <AgentSummaryMarkdownDocument
                        source={section.source}
                        {...markdownProps}
                      />
                    </div>
                  </section>
                );
              })}
            </div>
          </>
        ) : (
          <AgentSummaryMarkdownDocument source={source} {...markdownProps} />
        )}
      </Suspense>
    </div>
  );
}
